import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const EDITIONS = ["luxury", "solo", "wine", "escape", "adventure"];
const sel = "rounded-sm border border-stone-300 bg-white px-3 py-2 text-sm";
const blank = { destination: "", edition: "luxury", tier: "premium", season: "Spring", region: "" };

export const AddFounderRow = ({ reload }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const add = async () => {
    setBusy(true);
    try { await http.post("/register/rows", f); toast.success(`${f.destination} added to the Founder's Register`); setF(blank); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="founder-add-row" className="rounded-xl border border-[#E8DDC4] bg-[#FBF7EC] p-5 mb-6">
      <div className="eyebrow mb-3">Add a place you've been</div>
      <div className="flex flex-wrap gap-2">
        <input data-testid="founder-add-destination" value={f.destination} onChange={set("destination")} onKeyDown={(e) => e.key === "Enter" && f.destination.trim() && add()} placeholder="Destination, e.g. Kyoto, Japan" className={`${sel} w-72`} />
        <select data-testid="founder-add-edition" className={`${sel} capitalize`} value={f.edition} onChange={set("edition")}>{EDITIONS.map((e) => <option key={e} value={e}>{e}</option>)}</select>
        <select data-testid="founder-add-tier" className={sel} value={f.tier} onChange={set("tier")}>{["mini-break", "premium", "ultra", "elite"].map((t) => <option key={t}>{t}</option>)}</select>
        <select data-testid="founder-add-season" className={sel} value={f.season} onChange={set("season")}>{["Spring", "Summer", "Autumn", "Winter"].map((s) => <option key={s}>{s}</option>)}</select>
        <input data-testid="founder-add-region" value={f.region} onChange={set("region")} placeholder="Region (optional)" className={`${sel} w-44`} />
        <button data-testid="founder-add-submit" disabled={busy || !f.destination.trim()} onClick={add} className="inline-flex items-center gap-1.5 rounded-sm bg-[#2B2A26] text-white px-4 py-2 text-sm font-bold hover:bg-black disabled:opacity-40"><Plus size={14} /> Add row</button>
      </div>
    </div>
  );
};

export const EditRow = ({ row, onClose, reload }) => {
  const [f, setF] = useState({ destination: row.destination, edition: row.edition, tier: row.tier || "premium", season: row.season || "Spring", region: row.region === "—" ? "" : row.region || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await http.patch(`/register/rows/${row.id}`, f); toast.success("Row updated"); reload(); onClose(); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="register-edit-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit row</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="register-edit-destination" value={f.destination} onChange={set("destination")} className={`${sel} col-span-2`} />
          <select data-testid="register-edit-edition" className={`${sel} capitalize`} value={f.edition} onChange={set("edition")}>{EDITIONS.map((e) => <option key={e} value={e}>{e}</option>)}</select>
          <select data-testid="register-edit-tier" className={sel} value={f.tier} onChange={set("tier")}>{["mini-break", "premium", "ultra", "elite"].map((t) => <option key={t}>{t}</option>)}</select>
          <select data-testid="register-edit-season" className={sel} value={f.season} onChange={set("season")}>{["Spring", "Summer", "Autumn", "Winter"].map((s) => <option key={s}>{s}</option>)}</select>
          <input data-testid="register-edit-region" value={f.region} onChange={set("region")} placeholder="Region" className={sel} />
        </div>
        {row.kit && <p className="text-xs text-stone-500">Changing this row doesn't change the linked kit.</p>}
        <button data-testid="register-edit-save" disabled={busy || !f.destination.trim()} onClick={save} className="self-end rounded-sm bg-[#2B2A26] text-white px-4 py-2 text-sm font-bold hover:bg-black disabled:opacity-40">{busy ? "Saving…" : "Save"}</button>
      </DialogContent>
    </Dialog>
  );
};

export const RegisterTabs = ({ tab, setTab }) => (
  <div data-testid="register-tabs" className="flex gap-1 border-b border-stone-200 mb-8">
    {[["", "Kit Register"], ["founders", "Founder's Register"], ["extras", "Extras Register"], ["bespoke", "Bespoke Register"]].map(([k, l]) => (
      <button key={k || "main"} data-testid={`register-tab-${k || "main"}`} onClick={() => setTab(k)}
        className={`px-4 py-2.5 text-sm -mb-px border-b-2 transition-colors ${tab === k ? "border-[#C9A847] text-stone-900 font-semibold" : "border-transparent text-stone-500 hover:text-stone-800"}`}>{l}</button>
    ))}
  </div>
);
